import FileItem from '@/model/FileItem'
import type MainProject from '@/model/MainProject'
import { FileReader } from '@/platform'
import {
  readLegacyOuiIntoTarget,
  readVersionedOuiIntoTarget,
} from '@/model/ouiRuntime'

export type ParsedOuiResource = {
  name: string
  content: Uint8Array
}

export type ParsedOuiProject = MainProject

export type ParsedOuiFile = {
  format: 'legacy' | 'versioned'
  dataVersion: number
  project: MainProject
  resources: ParsedOuiResource[]
  fileItems: FileItem[]
}

function isVersionedOui(firstInt: number): boolean {
  return firstInt >= 115 && firstInt <= 300
}

function buildLegacyResources(bytes: Uint8Array, fileSectionStart: number, fileItems: FileItem[]): ParsedOuiResource[] {
  const resources: ParsedOuiResource[] = []
  for (const item of fileItems) {
    const start = fileSectionStart + Math.max(0, Number(item.point))
    const end = start + Math.max(0, Number(item.length))
    if (start < fileSectionStart || end > bytes.length || start >= end) {
      continue
    }
    resources.push({
      name: item.name,
      content: bytes.slice(start, end),
    })
  }
  return resources
}

function parseVersionedOui(
  bytes: Uint8Array,
  declaredVersion: number,
  target: MainProject,
  isNew: boolean,
): ParsedOuiFile {
  const reader = new FileReader(bytes)
  reader.readInt()
  target.dataVer = declaredVersion
  const fileItems = readVersionedOuiIntoTarget(target, reader, declaredVersion, isNew)

  return {
    format: 'versioned',
    dataVersion: declaredVersion,
    project: target,
    resources: target.FilesBuf.map((item) => ({
      name: item.name,
      content: item.content,
    })),
    fileItems,
  }
}

function parseLegacyOui(
  bytes: Uint8Array,
  dataVersion: number,
  target: MainProject,
  isNew: boolean,
): ParsedOuiFile {
  const reader = new FileReader(bytes)
  const payloadLength = reader.readInt()
  const payloadReader = reader.readObj(payloadLength)
  const fileSectionStart = reader.getPosition()
  target.dataVer = dataVersion
  const fileItems = readLegacyOuiIntoTarget(target, payloadReader, dataVersion, isNew)

  return {
    format: 'legacy',
    dataVersion,
    project: target,
    resources: buildLegacyResources(bytes, fileSectionStart, fileItems),
    fileItems,
  }
}

export function parseOuiFile(
  source: ArrayBuffer | Uint8Array,
  dataVersion: number,
  target: MainProject,
  isNew = false,
): ParsedOuiFile {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source)
  const reader = new FileReader(bytes)
  const firstInt = reader.readInt()

  if (isVersionedOui(firstInt)) {
    try {
      return parseVersionedOui(bytes, firstInt, target, isNew)
    } catch {
    }
  }

  return parseLegacyOui(bytes, dataVersion, target, isNew)
}

export const parseAutoOuiFile = parseOuiFile

